import { useRef, useEffect, useState, useCallback } from 'react'
import { Search, Plus, MoreHorizontal, X, Trash2, Maximize2, Minimize2, PanelLeft, PanelRight, MessageSquare } from 'lucide-react'
import { useChat } from '../hooks/useChat'
import { ChatMessage } from './ChatMessage'
import { ChatInput, ChatInputHandle } from './ChatInput'
import type { MicButtonHandle } from '@/shared/components/MicButton'
import { useChatPanel } from '@/shared/hooks/useMobileMenu'
import { eventBus } from '@/shared/lib/eventBus'
import { useVoiceChat } from '@/shared/hooks/useVoiceChat'
import { getAgentDefinitions, type AgentDefinition } from '@/shared/lib/api'
import { useChatStore } from '../store/useChatStore'
import { LoadingSpinner } from '@/shared/components/ui'
import { cn } from '@/shared/lib/cn'
import { parseApiDateTime } from '@/shared/lib/formatters'
import { useIsMobile } from '@/shared/hooks/useIsMobile'

/** Returns a human-readable time-ago label (e.g. "just now", "5m", "1d"). */
function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  const diff = Date.now() - parseApiDateTime(dateStr).getTime()
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d`
  const months = Math.floor(days / 30)
  return `${months}mo`
}

interface ChatPanelProps {
  /** Pre-select this agent definition when the panel first mounts.
   *  If the user changes the agent via the dropdown, their choice takes precedence. */
  defaultAgentDefinitionId?: number | null
}

export function ChatPanel({ defaultAgentDefinitionId }: ChatPanelProps = {}) {
  const {
    messages,
    isLoading,
    isStreaming,
    sendMessage,
    stopStream,
    model,
    setModel,
    agentDefinitionId,
    setAgentDefinitionId,
    isAgentLocked,
    workspaces,
    workspaceId,
    setWorkspaceId,
    conversations,
    currentConversationId,
    isLoadingConversations,
    streamingConversationIds,
    selectConversation,
    startNewConversation,
    removeConversation,
    renameConversation,
  } = useChat({ initialAgentDefinitionId: defaultAgentDefinitionId })

  const { closeChat, isFullscreen, toggleFullscreen, side, toggleSide, isChatOpen } = useChatPanel()
  const isMobileLayout = useIsMobile(768)

  const voiceChatState = useVoiceChat({ messages, isStreaming, sendMessage })
  const voiceChat = {
    isActive: voiceChatState.isVoiceChatActive,
    isListening: voiceChatState.isListening,
    isSpeaking: voiceChatState.isSpeaking,
    isTranscribing: voiceChatState.isTranscribing,
    onToggle: voiceChatState.toggleVoiceChat,
  }

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const chatInputRef = useRef<ChatInputHandle>(null)
  const micButtonRef = useRef<MicButtonHandle>(null)
  const [agentDefinitions, setAgentDefinitions] = useState<AgentDefinition[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editingTitleValue, setEditingTitleValue] = useState('')
  const [isNearBottom, setIsNearBottom] = useState(true)

  useEffect(() => {
    getAgentDefinitions(undefined, true)
      .then((defs) => {
        setAgentDefinitions(defs)
        // Default to assistant agent if no agent suggestion is already set
        const store = useChatStore.getState()
        if (store.suggestedAgentDefinitionId == null && defaultAgentDefinitionId == null) {
          const assistant = defs.find((a) => a.name === 'assistant_agent')
          if (assistant) {
            store.setSuggestedAgentDefinitionId(assistant.id)
          }
        }
      })
      .catch(() => {})
  }, [defaultAgentDefinitionId])

  // Track whether user is near bottom of scroll container
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight
      // User is "near bottom" if within 150px
      setIsNearBottom(distanceFromBottom < 150)
    }

    container.addEventListener('scroll', handleScroll)
    // Check initial state
    handleScroll()

    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  // Auto-scroll only when user is near bottom
  useEffect(() => {
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isNearBottom])

  // Soft keyboard scroll-into-view handler
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const handleResize = () => {
      // When keyboard opens, scroll latest message into view
      if (messagesEndRef.current) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        }, 100)
      }
    }

    viewport.addEventListener('resize', handleResize)
    return () => viewport.removeEventListener('resize', handleResize)
  }, [])

  // Auto-focus input when panel opens or conversation changes
  useEffect(() => {
    if (!isChatOpen) return
    const timer = setTimeout(() => chatInputRef.current?.focus(), 100)
    return () => clearTimeout(timer)
  }, [isChatOpen, currentConversationId])

  // Listen for cross-component "new chat" events (e.g. from command palette)
  useEffect(() => {
    return eventBus.on('chat:newConversation', () => {
      if (messages.length === 0) return
      startNewConversation()
    })
  }, [messages.length, startNewConversation])

  // Compute filtered conversations (needs to be before keyboard shortcut hook)
  const filteredConversations = conversations.filter((c) => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return (
      (c.title ?? '').toLowerCase().includes(q) ||
      (c.last_message_preview ?? '').toLowerCase().includes(q)
    )
  })

  // Keyboard shortcuts: Cmd/Ctrl+M to toggle microphone, Ctrl+H/L for chat navigation, Ctrl+Up/Down for chat navigation, Cmd/Ctrl+Shift+F to toggle fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip keyboard shortcuts when user is typing in an input, textarea, or contenteditable
      const target = e.target as HTMLElement
      const isTyping = target.tagName === 'INPUT' ||
                       target.tagName === 'TEXTAREA' ||
                       target.isContentEditable

      // Check if a modal is open by looking for role="dialog"
      const isModalOpen = document.querySelector('[role="dialog"]') !== null

      // Cmd+Shift+F (Mac) or Ctrl+Shift+F (Windows/Linux) to toggle fullscreen chat
      // Only active when the chat panel is open
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === 'KeyF' && isChatOpen) {
        e.preventDefault()
        toggleFullscreen()
        return
      }

      // Cmd+M (Mac) or Ctrl+M (Windows/Linux) to toggle microphone
      if ((e.metaKey || e.ctrlKey) && e.code === 'KeyM') {
        e.preventDefault()
        micButtonRef.current?.toggle()
        return
      }

      // Ctrl+H: Navigate to previous chat (vim-style)
      if (e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === 'KeyH' && !isTyping && !isModalOpen) {
        e.preventDefault()
        if (filteredConversations.length === 0) return

        const currentIndex = filteredConversations.findIndex((c) => c.id === currentConversationId)
        if (currentIndex === -1 && filteredConversations.length > 0) {
          // No conversation selected — select the first one
          selectConversation(filteredConversations[0].id)
        } else if (currentIndex > 0) {
          // Move up the list
          selectConversation(filteredConversations[currentIndex - 1].id)
        }
        return
      }

      // Ctrl+L: Navigate to next chat (vim-style)
      if (e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === 'KeyL' && !isTyping && !isModalOpen) {
        e.preventDefault()
        if (filteredConversations.length === 0) return

        const currentIndex = filteredConversations.findIndex((c) => c.id === currentConversationId)
        if (currentIndex === -1 && filteredConversations.length > 0) {
          // No conversation selected — select the first one
          selectConversation(filteredConversations[0].id)
        } else if (currentIndex < filteredConversations.length - 1) {
          // Move down the list
          selectConversation(filteredConversations[currentIndex + 1].id)
        }
        return
      }

      // Ctrl+ArrowUp: Navigate to previous chat
      if (e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === 'ArrowUp' && !isTyping && !isModalOpen) {
        e.preventDefault()
        if (filteredConversations.length === 0) return

        const currentIndex = filteredConversations.findIndex((c) => c.id === currentConversationId)
        if (currentIndex === -1 && filteredConversations.length > 0) {
          // No conversation selected — select the first one
          selectConversation(filteredConversations[0].id)
        } else if (currentIndex > 0) {
          // Move up the list
          selectConversation(filteredConversations[currentIndex - 1].id)
        }
        return
      }

      // Ctrl+ArrowDown: Navigate to next chat
      if (e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === 'ArrowDown' && !isTyping && !isModalOpen) {
        e.preventDefault()
        if (filteredConversations.length === 0) return

        const currentIndex = filteredConversations.findIndex((c) => c.id === currentConversationId)
        if (currentIndex === -1 && filteredConversations.length > 0) {
          // No conversation selected — select the first one
          selectConversation(filteredConversations[0].id)
        } else if (currentIndex < filteredConversations.length - 1) {
          // Move down the list
          selectConversation(filteredConversations[currentIndex + 1].id)
        }
        return
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [filteredConversations, currentConversationId, selectConversation, toggleFullscreen, isChatOpen])

  const currentConversation = conversations.find((c) => c.id === currentConversationId)
  const panelTitle = currentConversation?.title?.trim() || 'New Chat'
  const isEmpty = messages.length === 0

  // Guard: don't allow creating a new chat when the current conversation is already empty.
  // This mirrors how Claude/ChatGPT/Gemini handle it — reuse the blank slate rather than
  // stacking empty conversations.
  const handleNewChat = useCallback(() => {
    if (isEmpty) return
    startNewConversation()
    setMobileSidebarOpen(false)
  }, [isEmpty, startNewConversation])

  return (
    <div className={cn(
      'h-full overflow-hidden bg-[#0D0D0D]',
      isMobileLayout ? '' : `flex ${side === 'right' ? 'flex-row-reverse' : 'flex-row'}`
    )}>
      {/* ── Mobile sidebar backdrop ─────────────────────────────── */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ──────────────────────────────────────────────── */}
      <div
        className={cn(
          'flex flex-col bg-[#0D0D0D]',
          isMobileLayout
            ? '' // mobile sizing handled via inline style below
            : cn(
                'w-[160px] md:w-[200px] lg:w-[240px] flex-shrink-0',
                side === 'right' ? 'border-l border-white/5' : 'border-r border-white/5',
              ),
        )}
        // On mobile, use inline styles for fixed overlay — CSS media-query-based
        // `position: fixed` is unreliable inside transformed containers.
        style={isMobileLayout ? {
          position: 'fixed',
          top: 0,
          bottom: 0,
          width: '280px',
          maxWidth: '85vw',
          height: '100dvh',
          zIndex: 50,
          [side === 'right' ? 'right' : 'left']: 0,
          transform: mobileSidebarOpen ? 'translateX(0)' : side === 'right' ? 'translateX(100%)' : 'translateX(-100%)',
          transition: 'transform 200ms ease-out',
          boxShadow: mobileSidebarOpen ? '0 25px 50px -12px rgba(0,0,0,0.25)' : 'none',
          borderLeft: side === 'right' ? '1px solid rgba(255,255,255,0.05)' : undefined,
          borderRight: side === 'left' ? '1px solid rgba(255,255,255,0.05)' : undefined,
        } : undefined}
      >
        {/* Search */}
        <div className="p-3 pb-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/5 rounded-lg pl-7 pr-3 py-2 text-base max-sm:text-base sm:text-xs text-[#EEEEEE] placeholder-white/30 outline-none focus:bg-white/8 transition-colors"
            />
          </div>
        </div>

        {/* New Chat button */}
        <div className="px-3 pb-3">
          <button
            onClick={handleNewChat}
            disabled={isEmpty}
            title={isEmpty ? 'Send a message first to start a new chat' : 'New chat'}
            className="w-full flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg py-2 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/10"
          >
            <Plus size={13} />
            New Chat
          </button>
        </div>

        {/* Conversations section */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {isLoadingConversations ? (
            <div className="px-3 py-4">
              <LoadingSpinner variant="text" size="sm" message="Loading..." />
            </div>
          ) : (
            <>
              {filteredConversations.length > 0 && (
                <>
                  <p className="px-3 pb-1 text-[10px] uppercase tracking-widest text-white/30 font-medium">
                    Conversations
                  </p>
                  <div className="space-y-0.5 px-2">
                    {filteredConversations.map((conv) => {
                      const isActive = conv.id === currentConversationId
                      const isStreamingThis = streamingConversationIds.has(conv.id)
                      return (
                        <div
                          key={conv.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => { selectConversation(conv.id); setMobileSidebarOpen(false) }}
                          onKeyDown={(e) => e.key === 'Enter' || e.key === ' ' ? selectConversation(conv.id) : undefined}
                          className={cn(
                            'group w-full text-left px-2 py-2 rounded-lg transition-colors cursor-pointer',
                            isActive ? 'bg-white/[0.08]' : 'hover:bg-white/5'
                          )}
                        >
                          <div className="flex items-start gap-2">
                            {/* Circle / streaming indicator */}
                            <div className="flex-shrink-0 mt-0.5">
                              {isStreamingThis ? (
                                <span className="block w-2 h-2 rounded-full bg-white/70 animate-pulse" />
                              ) : (
                                <span className={cn(
                                  'block w-2 h-2 rounded-full border',
                                  isActive ? 'border-white/70' : 'border-white/20'
                                )} />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-1">
                                <span className={cn(
                                  'text-xs truncate',
                                  isActive ? 'text-[#EEEEEE]' : 'text-white/70'
                                )}>
                                  {(conv.title?.trim() || 'New conversation').slice(0, 32)}
                                </span>
                                <span className="text-[10px] text-white/30 flex-shrink-0">
                                  {timeAgo(conv.updated_at)}
                                </span>
                              </div>
                              {conv.last_message_preview && (
                                <p className="text-[10px] text-white/30 truncate mt-0.5 leading-tight">
                                  {conv.last_message_preview}
                                </p>
                              )}
                            </div>

                            {/* Delete button — appears on hover */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                removeConversation(conv.id)
                              }}
                              className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded text-white/30 hover:text-white/70 hover:bg-white/10 transition-all"
                              title="Delete conversation"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}

              {/* Archived section label */}
              <div className="px-3 pt-4 pb-2">
                <p className="text-[10px] uppercase tracking-widest text-white/20 font-medium">
                  Archived
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Right Main Area ───────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden bg-[#111111]">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 flex-shrink-0 safe-top">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden p-1.5 rounded-md hover:bg-white/8 text-white/40 hover:text-white/70 transition-colors flex-shrink-0"
              title="Conversations"
            >
              <MessageSquare size={15} />
            </button>
            {isEditingTitle ? (
              <input
                type="text"
                value={editingTitleValue}
                onChange={(e) => setEditingTitleValue(e.target.value)}
                onBlur={() => {
                  if (currentConversationId) {
                    renameConversation(currentConversationId, editingTitleValue || panelTitle)
                  }
                  setIsEditingTitle(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur()
                  } else if (e.key === 'Escape') {
                    setIsEditingTitle(false)
                  }
                }}
                className="text-sm font-medium text-white/90 bg-white/8 border border-white/15 rounded px-2 py-0.5 outline-none min-w-0 w-48 max-w-[180px]"
                autoFocus
              />
            ) : (
              <span
                className="text-sm font-medium text-white/70 truncate cursor-default select-none"
                title="Double-click to rename"
                onDoubleClick={() => {
                  if (!currentConversationId) return
                  setEditingTitleValue(currentConversation?.title?.trim() || '')
                  setIsEditingTitle(true)
                }}
              >
                {panelTitle}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={handleNewChat}
              disabled={isEmpty}
              title={isEmpty ? 'Send a message first to start a new chat' : 'New chat'}
              className="p-1.5 rounded-md hover:bg-white/8 text-white/40 hover:text-white/70 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <Plus size={15} />
            </button>
            <button
              className="p-1.5 rounded-md hover:bg-white/8 text-white/40 hover:text-white/70 transition-colors"
              title="More options"
            >
              <MoreHorizontal size={15} />
            </button>
            <button
              onClick={toggleFullscreen}
              className="hidden sm:block p-1.5 rounded-md hover:bg-white/8 text-white/40 hover:text-white/70 transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen
                ? <Minimize2 size={15} />
                : <Maximize2 size={15} />}
            </button>
            <button
              onClick={toggleSide}
              className="hidden sm:block p-1.5 rounded-md hover:bg-white/8 text-white/40 hover:text-white/70 transition-colors"
              aria-label={side === 'right' ? 'Move to left side' : 'Move to right side'}
              title={side === 'right' ? 'Move to left side' : 'Move to right side'}
            >
              {side === 'right' ? <PanelLeft size={15} /> : <PanelRight size={15} />}
            </button>
            <button
              onClick={closeChat}
              className="p-1.5 rounded-md hover:bg-white/8 text-white/40 hover:text-white/70 transition-colors"
              aria-label="Close chat"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Content area */}
        {isEmpty ? (
          /* Empty / new-chat state — large centered input */
          <div className="flex-1 flex flex-col items-stretch justify-center px-6 py-8 min-w-0">
            <ChatInput
              ref={chatInputRef}
              micButtonRef={micButtonRef}
              onSend={sendMessage}
              onStop={stopStream}
              disabled={isLoading}
              model={model}
              onModelChange={setModel}
              agentDefinitionId={agentDefinitionId}
              onAgentChange={setAgentDefinitionId}
              agentDefinitions={agentDefinitions}
              isAgentLocked={isAgentLocked}
              workspaceId={workspaceId}
              onWorkspaceChange={setWorkspaceId}
              workspaces={workspaces}
              voiceChat={voiceChat}
            />
          </div>
        ) : (
          /* Active conversation */
          <>
            <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4" style={{ scrollbarGutter: 'stable' }}>
              {messages.map((msg, index) => (
                <ChatMessage key={index} message={msg} conversationId={currentConversationId ?? 0} />
              ))}

              {/* Thinking indicator — shown while waiting for first token */}
              {isLoading && !isStreaming && (
                <div className="mb-4">
                  <div className="bg-white/5 border border-white/5 text-[#EEEEEE] mr-auto rounded-xl rounded-bl-sm p-3 max-w-[85%] inline-block">
                    <LoadingSpinner variant="dots" size="sm" message="Thinking..." />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area — compact at bottom */}
            <div className="flex-shrink-0 px-4 py-3 border-t border-white/5 safe-bottom min-w-0">
              <ChatInput
                ref={chatInputRef}
                micButtonRef={micButtonRef}
                onSend={sendMessage}
                onStop={stopStream}
                disabled={isLoading}
                model={model}
                onModelChange={setModel}
                agentDefinitionId={agentDefinitionId}
                onAgentChange={setAgentDefinitionId}
                agentDefinitions={agentDefinitions}
                isAgentLocked={isAgentLocked}
                workspaceId={workspaceId}
                onWorkspaceChange={setWorkspaceId}
                workspaces={workspaces}
                voiceChat={voiceChat}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
